import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { IOmniaSession, IOmniaShell, IParam } from "@omnia/types";
import { HoldingEditModel, HoldingEditStatus, IHoldingService } from "../types";
import { getBusinessList, IHolding } from "@omnia/shared/holding";
import { HoldingEditTabHolding } from "./HoldingEditTabHolding";
import { HoldingEditTabWeather } from "./HoldingEditTabWeather";
import { useAsyncValue } from "@omnia/shared/react/hooks";
import { param } from "@omnia/shared/generated/labels";
import { isAgronomist, isHoldingReadOnly, isInternalRole } from "@omnia/shared/session/utils";
import { RoleId } from "@omnia/shared/user";
import { CropYear } from "@omnia/shared/cropYear";
import { isRegionInNvz } from "@omnia/shared/nutrition/nvz";
import { RegionId } from "@omnia/shared/generated/enums";
import { HoldingData, pkModelType } from "./types";
import { isArrayEmpty, range, sum } from "@omnia/shared/utils/array";
import { IHoldingWeather, IWeatherStation } from "@omnia/shared/weather";
import { ITown } from "@omnia/shared/town";
import { showYesNo } from "@omnia/shared/react/msgbox";
import { validateHoldingData } from "./validation";
import { isUndefinedOrNull } from "@omnia/shared/utils/utils";

export interface HoldingEditComponentHandle {
    saveHolding: () => ng.IPromise<any>;
}

interface Props {
    shell: IOmniaShell
    session: IOmniaSession
    holdingService: IHoldingService
    holding: IHolding
    onIsValidChange?: (isValid: boolean) => void
}

type tabNameType = "holding" | "weather";

export const HoldingEditComponent = forwardRef<HoldingEditComponentHandle, Props>(({ shell, session, holdingService: svc, holding, onIsValidChange }: Props, ref) => {
    const [activeTab, setActiveTab] = useState<tabNameType>("holding")
    const [holdingData, setHoldingData] = useState<HoldingData>();

    const isSessionSetUp = useMemo(() => {
        return !isUndefinedOrNull(session?.holdingId);
    }, [session?.holdingId]);

    // region setup target
    const target = useAsyncValue(() => svc.ensureInit().then(() => {
        if (!holding) {
            return undefined;
        }
        
        return svc.getStatus(holding.HoldingId).then((r: HoldingEditStatus) => {

            // setupRegions
            const regionIds = (shell?.config?.regions?.length > 0) ? new Set(shell.config.regions) : undefined;
            if (regionIds && holding?.Region) {
                regionIds.add(holding.Region);
            }
            const regions: IParam[] = regionIds ? param.RegionId().filter(p => regionIds.has(p.value)) : undefined;
            
            // setup Holding info
            const canEditAccountNumber = getCanEditAccountNumber(session);
            const enableReportingOption = getCanSetReportingOption(session);
            const isNew = !holding.HoldingId;
            const readOnly = isNew
                ? session.isReadOnly(p => p.holdings)
                : isHoldingReadOnly(session, holding)
                
            const croppingYears = session.getCroppingYears();
            
            const canChangeRegion = r.CanChangeRegion;
            
            const initYears = startingCropYears(holding, croppingYears);
            const firstCroppingYear = isNew
                ? initYears[initYears.length - 2].Year
                : Number(Math.min.apply(Math, holding.CropYears.map(c => c.YearId)));
            
            const holdingClone = structuredClone(holding);
            const address = holdingClone.Address;
            let region = holdingClone.Region;
            if (!region && regions?.length > 0) {
                region = regions[0].value;
            }
            const nvz = isNew ? true : holdingClone.NVZ;
            
            const includeInReports = !r.ExcludeFromUserReports;
            
            if ((!canEditAccountNumber) && isNew) {
                holdingClone.AccountNumber = session.holding?.AccountNumber
            }
            
            // setupBusiness
            const businessInfo = getSetupBusiness(shell, session, holdingClone);
            // setupCurrentYear
            const currentYearInfo = setupCurrentYear(svc, session, holding, isNew);
                
            const towns = r.Towns.filter(t => (!t.Deleted) || (t.TownId == holding.TownId));
            const stations = r.WeatherStations ?? [];

            const town = towns.find(t => t.TownId === holdingClone.TownId);
            const [townStations, station] = onTownChanged(stations, town, holding.WeatherStationId);
            
            let weatherData = r.Weather ?? [];
            const customWeather = weatherData.length > 0;
            if (!customWeather) {
                weatherData = getStationWeatherData(station, holding.HoldingId)
            }

            const features = shell.config.features;
            const enableNvz = features.nvz;
            const canChangeNvz = enableNvz && (!isRegionInNvz(region));
            const isInScotland = region === RegionId.Scotland;
            
            const initialState: HoldingData = {
                annualRainfall: getAnnualRainfall(weatherData),
                business: businessInfo.business,
                businesses: businessInfo.businesses,
                canChangeNvz: canChangeNvz,
                canChangeRegion: canChangeRegion,
                canEditAccountNumber: canEditAccountNumber,
                canSelectPkModel: isInScotland,
                canSetBusiness: !session.userBusiness,
                customWeather: customWeather,
                enableBusiness: businessInfo.enableBusiness,
                enableNvz: enableNvz,
                enablePostTown: features.holding.postTown,
                enableReportingOption: enableReportingOption,
                firstCroppingYear: firstCroppingYear,
                grasslandDerogation: currentYearInfo.grasslandDerogation ?? false,
                grasslandDerogationNumber: currentYearInfo.grasslandDerogationNumber,
                hasPreviousYear: currentYearInfo.hasPreviousYear,
                holdingAccountNumber: holdingClone.AccountNumber ?? '',
                holdingAddress: address,
                holdingId: holdingClone.HoldingId,
                holdingPSorpCapacity: holdingClone.PSorpCapacity,
                holdingStationId: holdingClone.WeatherStationId,
                holdingTradingName: holdingClone.TradingName,
                includeInReports: includeInReports,
                isNew: isNew,
                nvz: nvz,
                nvzLink: getNvzLink(isInScotland),
                pkModel: currentYearInfo.pkModel,
                readOnly: readOnly,
                region: region,
                regions: regions,
                startingCropYears: initYears,
                station: station,
                stations: stations,
                totalArea: currentYearInfo.totalArea,
                totalAreaCalculated: currentYearInfo.totalAreaCalculated,
                totalAreaPreviousYear: currentYearInfo.totalAreaPreviousYear,
                town: town,
                towns: towns,
                townStations: townStations,
                weatherData: weatherData,
                year: currentYearInfo.year,
                validationMessages: []
            };

            initialState.validationMessages = validateHoldingData(initialState, isSessionSetUp);

            // initial state
            setHoldingData(initialState);

            return holdingClone;
        });
    }), [svc, svc.yearId, svc.stateVersion, session, holding, onIsValidChange, isSessionSetUp]);

    // region update state
    const updateHoldingData = useCallback((updates: Partial<HoldingData>) => {
        setHoldingData(prev => {
            const next = { ...prev, ...updates };
            
            // town has changed
            if (next.town !== undefined && next.town.TownId !== prev.town?.TownId && holding) {
                const [newTownStations, newStation] = onTownChanged(prev.stations, next.town, holding.WeatherStationId);
                next.townStations = newTownStations;
                next.station = newStation;
                if (next.isNew && next.town.Region) {
                    next.region = next.town.Region ;
                }
            }

            // the region has changed
            if (next.region !== prev.region) {
                if (isRegionInNvz(next.region)) {
                    next.nvz = true;
                }
                next.canChangeNvz = next.enableNvz && !isRegionInNvz(next.region);
                next.canSelectPkModel = next.region === RegionId.Scotland;
                next.nvzLink = getNvzLink(next.region === RegionId.Scotland);
            }

            // custom weather flag has changed
            if (prev.customWeather !== next.customWeather && holding) {
                if (next.customWeather) {
                    next.weatherData = range(12).map(m => ({
                        HoldingId: holding.HoldingId,
                        Year: 2010,
                        Month: m + 1,
                        Rainfall: null
                    }))
                } else {
                    next.station = next.station || (next.townStations.length ? next.townStations[0] : null)
                    next.weatherData = getStationWeatherData(next.station, holding.HoldingId)
                }
            }

            // station has changed
            if (prev.station?.WeatherStationId !== next.station?.WeatherStationId && holding) {
                if (next.station && (!next.customWeather)) {
                    next.weatherData = getStationWeatherData(next.station, holding.HoldingId); 
                }
            }
            
            // weather data has changed
            if (next.weatherData !== prev.weatherData || next.customWeather !== prev.customWeather) {
                next.annualRainfall = getAnnualRainfall(next.weatherData);
            }

            // pkModel has changed
            if (next.pkModel !== prev.pkModel) {
                if (next.pkModel !== "SRUC") {
                    next.holdingPSorpCapacity = null;
                }
                if (next.pkModel == "SRUC" && !next.holdingPSorpCapacity) {
                    next.holdingPSorpCapacity = 1;
                }
            }

            // if the derogation has been unticked, reset the number field
            if (prev.grasslandDerogation && !next.grasslandDerogation && !!next.grasslandDerogationNumber) {
                next.grasslandDerogationNumber = null;
            }

            next.validationMessages = validateHoldingData(next, isSessionSetUp);

            return next;
        });
    }, [holding?.HoldingId, holding?.WeatherStationId, onIsValidChange, session, isSessionSetUp]);

    useEffect(() => {
        if (holdingData) {
            const valid = holdingData.validationMessages.length === 0;
            onIsValidChange?.(valid);
        }
    }, [holdingData, onIsValidChange]);
    
    // region save
    const commit = (changeNVZOnAllFields: boolean): ng.IPromise<any> => {

        const { features: { nvz } } = shell.config;

        target.TradingName = holdingData.holdingTradingName;
        target.AccountNumber = holdingData.holdingAccountNumber;
        target.Address = holdingData.holdingAddress;
        target.TownId = holdingData.town?.TownId;
        target.Town = holdingData.town;
        target.BusinessId = holdingData.business?.BusinessId;
        target.Business = holdingData.business;
        target.Region = holdingData.region;
        target.NVZ = holdingData.nvz && nvz
        target.PSorpCapacity = holdingData.holdingPSorpCapacity;


        if ((!target.AgronomistId) && isAgronomist(session)) {
            target.AgronomistId = session.currentUser.Id
        }

        const holdingWeather = holdingData.customWeather ? holdingData.weatherData : []

        if (!isArrayEmpty(holdingWeather)) {
            target.WeatherStationId = null
        }
        else if (holdingData.stations) {
            target.WeatherStationId =
                !holdingData.station || holdingData.station.WeatherStationId === target.Town.WeatherStationId
                    ? null
                    : holdingData.station.WeatherStationId;
        }

        const model: HoldingEditModel = {
            holding: target,
            firstCroppingYear: holdingData.firstCroppingYear,
            changeNVZOnAllFields,
            totalArea: holdingData.totalArea,
            grasslandDerogationNumber: holdingData.grasslandDerogationNumber,
            holdingWeather,
            pkModel: holdingData.pkModel,
            excludeFromUserReports: !holdingData.includeInReports
        }

        return svc.saveHolding(model)
            .then(() => session.refreshHoldings())
            .then(() => {
                return svc.refresh();
            });
    }

    const saveHolding = () => {

        if (holding.HoldingId) {
            const regInNvz = isRegionInNvz(holdingData.region);
            if (!regInNvz) {
                if (holding && (holding.NVZ !== holdingData.nvz)) {
    
                    const msg = "You have changed the NVZ value. Do you want to change the NVZ on all the fields of the " + holding.TradingName + " holding ?"
    
                    return showYesNo(msg).then(r => commit(r))
    
                }
            }
        }

        return commit(false)
    }

    useImperativeHandle(ref, () => ({
        saveHolding
    }), [saveHolding]);

    return !!target ? (
        <form id="holding-edit-form" 
            className={`omnia-holding-edit material-form ${(holdingData?.validationMessages?.length > 0) ? 'invalid' : ''}`} 
            noValidate={true} 
            autoComplete="off">

            <fieldset disabled={holdingData?.readOnly}>
                <div className="row tabs">
                    <div className="col-md-12">
                        <div>
                            <ul className="nav nav-tabs">
                                <li className={(activeTab === "holding") ? 'active' : ''}>
                                    <a onClick={() => setActiveTab("holding")}>HOLDING</a>
                                </li>
                                {isSessionSetUp &&
                                    <li className={(activeTab === "weather") ? 'active' : ''}>
                                        <a onClick={() => setActiveTab("weather")}>WEATHER</a>
                                    </li>
                                }
                            </ul>
                        </div>

                        <div className="tab-content">
                            {(activeTab === "holding") &&
                                <HoldingEditTabHolding data={holdingData} 
                                    onUpdate={updateHoldingData} />
                            }
                            {(activeTab === "weather") &&
                                <HoldingEditTabWeather data={holdingData} onUpdate={updateHoldingData} />
                            }
                        </div>

                    </div>

                </div>

            </fieldset>

        </form>)
        : null;
});

// region Independent Functions

const getAnnualRainfall = (wdata: IHoldingWeather[]) => {
    return !!wdata
        ? wdata.length ? Math.round(sum(wdata, d => d.Rainfall) * 10) / 10 : 0
        : 0;
}

const getNvzLink = (isInScotland: boolean) => {
    return isInScotland
        ? "http://www.gov.scot/Topics/farmingrural/Agriculture/Environment/NVZintro"
        : "https://environment.data.gov.uk/farmers";
}

const getCanEditAccountNumber = (s: IOmniaSession) => {
    return isInternalRole(s)
}

const getCanSetReportingOption = ({ currentUserRole: role }: IOmniaSession) => {
    return (role === RoleId.Agronomist) || (role === RoleId.Manager)
}

const startingCropYears = (holding: IHolding, croppingYears: CropYear[]): CropYear[] => {
    if (holding && holding.HoldingId) {
        let initialYear = Math.min.apply(Math, holding.CropYears.map(c => c.YearId))
        return croppingYears.filter(c => c.Year <= initialYear)
    }

    return croppingYears
}

const getStationWeatherData = (station: IWeatherStation, holdingId: number) => {
    return station?.Weather.map(w => {
        return {
            HoldingId: holdingId,
            Year: w.Year,
            Month: w.Month,
            Rainfall: w.RainfallLongTermAvg
        }
    }).sort((a, b) => a.Month > b.Month ? 1 : a.Month < b.Month ? -1 : 0)
}

const getSetupBusiness = (shell: IOmniaShell, session: IOmniaSession, holding: IHolding) => {

    const features = shell.config.features;
    let businesses = getBusinessList()

    const business = session.userBusiness
        ? session.userBusiness
        : businesses.find(b => b.BusinessId === holding.BusinessId);

    const id = business && business.BusinessId;

    businesses = businesses.filter(b => (b.BusinessId < 1000) || (id == b.BusinessId));

    const businessIdsInfo = features.holding.business;

    if (businessIdsInfo.ids) {
        businesses = businesses.filter(b => businessIdsInfo.ids.some(id => id == b.BusinessId) || (id == b.BusinessId));
    }

    const enableBusiness = businessIdsInfo.available;

    return {
        businesses: businesses,
        business: business,
        enableBusiness: enableBusiness,
    }
}

const setupCurrentYear = (svc: IHoldingService, session: IOmniaSession, holding: IHolding, isNew: boolean) => {

    const years = holding.CropYears ?? []
    const year = session.yearId
    let pkModel: pkModelType = "ADAS"

    if (isNew) {
        return {
            pkModel,
            years,
            year
        }
    }

    const currentYear = years.find(c => c.YearId === year)
    const previousYear = years.find(c => c.YearId === year - 1)

    // Total area and grassland derogation
    const fieldsArea = Math.round(svc.getFieldsArea() * 100) / 100

    const hasPreviousYear = !!previousYear
    if (hasPreviousYear) {
    }
    const totalAreaPreviousYear: number = hasPreviousYear
        ? (!!previousYear.TotalArea ? previousYear.TotalArea : fieldsArea)
        : undefined;

    const totalAreaCalculated = fieldsArea
    const totalArea = currentYear ? currentYear.TotalArea : 0
    const grasslandDerogationNumber = currentYear ? currentYear.GrasslandDerogationNumber : null
    const grasslandDerogation = !!grasslandDerogationNumber

    // PK Model
    pkModel = (currentYear?.PkModel ?? previousYear?.PkModel) || "ADAS";

    return {
        pkModel,
        years,
        year,
        hasPreviousYear,
        totalAreaPreviousYear,
        totalAreaCalculated,
        totalArea,
        grasslandDerogationNumber,
        grasslandDerogation,
    }

}

const onTownChanged = (stations: IWeatherStation[], town: ITown, originalStationId: number): [townStations: IWeatherStation[], station: IWeatherStation | null] => {

    if (!stations) {
        return undefined;
    }

    if (!town) {
        return [ [], null ];
    }

    let townStations = town.OtherWeatherStations
        .sort((a, b) => a.Index > b.Index ? 1 : a.Index < b.Index ? -1 : 0)
        .map(o => stations.find(s => o.StationId === s.WeatherStationId))
        .filter(s => !!s)

    if (town.WeatherStationId && (!townStations.find(s => s.WeatherStationId == town.WeatherStationId))) {
        townStations = [
            stations.find(s => s.WeatherStationId == town.WeatherStationId)
        ].concat(townStations)
    }

    townStations = townStations.filter(s => !!s)

    const currentStationId = originalStationId

    let station: IWeatherStation = null;
    if (currentStationId) {
        station = townStations.find(s => s.WeatherStationId === currentStationId)
    }

    station ??= townStations[0]

    return [ townStations, station ];
}